import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * UI Store
 * 
 * Manages UI-related global state: modals, toasts, sidebars, and preferences.
 */

// ═══════════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════════

export type ModalType = 
  | 'login' | 'signup' | 'forgotPassword' | 'resetPassword'
  | 'listing' | 'checkout' | 'confirmPurchase'
  | 'sellerOnboarding' | 'createListing' | 'editListing' | 'transferTickets'
  | 'support' | 'confirmAction' | null;

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
  action?: ToastAction;
}

export type EventViewMode = 'grid' | 'list' | 'map';
export type ListingViewMode = 'grid' | 'list';

export interface UIState {
  activeModal: ModalType;
  modalData: Record<string, unknown> | null;
  isSidebarOpen: boolean;
  isMobileMenuOpen: boolean;
  toasts: Toast[];
  currency: string;
  locale: string;
  timezone: string;
  eventViewMode: EventViewMode;
  listingViewMode: ListingViewMode;
  hasCompletedOnboarding: boolean;
  hasSeenSellerPrompt: boolean;
  
  openModal: (modal: Exclude<ModalType, null>, data?: Record<string, unknown>) => void;
  closeModal: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  setCurrency: (currency: string) => void;
  setLocale: (locale: string) => void;
  setTimezone: (timezone: string) => void;
  setEventViewMode: (mode: EventViewMode) => void;
  setListingViewMode: (mode: ListingViewMode) => void;
  completeOnboarding: () => void;
  dismissSellerPrompt: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════════

function generateId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York';
  }
}

const DEFAULT_TOAST_DURATION = 5000;
const toastTimers = new Map<string, NodeJS.Timeout>();

function scheduleToastRemoval(id: string, duration: number, removeToast: (id: string) => void): void {
  if (duration <= 0) return;
  const timer = setTimeout(() => {
    removeToast(id);
    toastTimers.delete(id);
  }, duration);
  toastTimers.set(id, timer);
}

function clearToastTimer(id: string): void {
  const timer = toastTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    toastTimers.delete(id);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════════════

export const useUIStore = create<UIState>()(
  persist(
    immer((set, get) => ({
      activeModal: null,
      modalData: null,
      isSidebarOpen: true,
      isMobileMenuOpen: false,
      toasts: [],
      currency: 'USD',
      locale: 'en-US',
      timezone: getDefaultTimezone(),
      eventViewMode: 'grid',
      listingViewMode: 'grid',
      hasCompletedOnboarding: false,
      hasSeenSellerPrompt: false,
      
      openModal: (modal, data) => set((state) => {
        state.activeModal = modal;
        state.modalData = data ?? null;
        state.isMobileMenuOpen = false;
      }),
      
      closeModal: () => set((state) => {
        state.activeModal = null;
        state.modalData = null;
      }),
      
      toggleSidebar: () => set((state) => {
        state.isSidebarOpen = !state.isSidebarOpen;
      }),
      
      setSidebarOpen: (open) => set((state) => {
        state.isSidebarOpen = open;
      }),
      
      toggleMobileMenu: () => set((state) => {
        state.isMobileMenuOpen = !state.isMobileMenuOpen;
      }),
      
      setMobileMenuOpen: (open) => set((state) => {
        state.isMobileMenuOpen = open;
      }),
      
      addToast: (toast) => {
        const id = generateId();
        const duration = toast.duration ?? DEFAULT_TOAST_DURATION;
        
        set((state) => {
          state.toasts.push({ ...toast, id, duration });
        });
        
        if (duration > 0) {
          scheduleToastRemoval(id, duration, get().removeToast);
        }
        
        return id;
      },
      
      removeToast: (id) => {
        clearToastTimer(id);
        set((state) => {
          state.toasts = state.toasts.filter(t => t.id !== id);
        });
      },
      
      clearToasts: () => {
        for (const id of toastTimers.keys()) {
          clearToastTimer(id);
        }
        set((state) => {
          state.toasts = [];
        });
      },
      
      setCurrency: (currency) => set((state) => { state.currency = currency; }),
      setLocale: (locale) => set((state) => { state.locale = locale; }),
      setTimezone: (timezone) => set((state) => { state.timezone = timezone; }),
      setEventViewMode: (mode) => set((state) => { state.eventViewMode = mode; }),
      setListingViewMode: (mode) => set((state) => { state.listingViewMode = mode; }),
      completeOnboarding: () => set((state) => { state.hasCompletedOnboarding = true; }),
      dismissSellerPrompt: () => set((state) => { state.hasSeenSellerPrompt = true; }),
    })),
    {
      name: 'tique-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currency: state.currency,
        locale: state.locale,
        timezone: state.timezone,
        eventViewMode: state.eventViewMode,
        listingViewMode: state.listingViewMode,
        hasCompletedOnboarding: state.hasCompletedOnboarding,
        hasSeenSellerPrompt: state.hasSeenSellerPrompt,
        isSidebarOpen: state.isSidebarOpen,
      }),
    }
  )
);

// ═══════════════════════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════════════════════

export const selectActiveModal = (state: UIState): ModalType => state.activeModal;
export const selectModalData = (state: UIState): Record<string, unknown> | null => state.modalData;
export const selectIsModalOpen = (modalType: Exclude<ModalType, null>) => 
  (state: UIState): boolean => state.activeModal === modalType;
export const selectToasts = (state: UIState): Toast[] => state.toasts;
export const selectIsSidebarOpen = (state: UIState): boolean => state.isSidebarOpen;
export const selectIsMobileMenuOpen = (state: UIState): boolean => state.isMobileMenuOpen;
export const selectPreferences = (state: UIState) => ({
  currency: state.currency,
  locale: state.locale,
  timezone: state.timezone,
  eventViewMode: state.eventViewMode,
  listingViewMode: state.listingViewMode,
});

// ═══════════════════════════════════════════════════════════════════════════════════
// TOAST HELPER HOOK
// ═══════════════════════════════════════════════════════════════════════════════════

export function useToast() {
  const addToast = useUIStore((state) => state.addToast);
  const removeToast = useUIStore((state) => state.removeToast);
  
  return {
    toast: addToast,
    dismiss: removeToast,
    success: (title: string, description?: string, duration?: number): string =>
      addToast({ type: 'success', title, description, duration }),
    error: (title: string, description?: string, duration?: number): string =>
      addToast({ type: 'error', title, description, duration }),
    warning: (title: string, description?: string, duration?: number): string =>
      addToast({ type: 'warning', title, description, duration }),
    info: (title: string, description?: string, duration?: number): string =>
      addToast({ type: 'info', title, description, duration }),
  };
}